import { Button } from '@client/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PREFERENCE_QUESTIONS } from './questions';

interface SummaryViewProps {
  preferences: PolicyPreferences;
  onEdit: () => void;
  variant?: 'default' | 'warning';
  warningMessage?: {
    title: string;
    description: string;
  };
}

function getPreferenceLabel(key: PolicyPreferenceKey, value: string | null): string {
  if (!value) return 'Not set';

  const question = PREFERENCE_QUESTIONS.find(q => q.key === key);
  const option = question?.options.find(o => o.value === value);
  return option?.label ?? value;
}

function getPreferencesSetCount(preferences: PolicyPreferences): number {
  let count = 0;
  if (preferences.deathBenefit !== null) count++;
  if (preferences.paymentStructure !== null) count++;
  if (preferences.benefitStructure !== null) count++;
  return count;
}

export function SummaryView({
  preferences,
  onEdit,
  variant = 'default',
  warningMessage,
}: SummaryViewProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const preferencesCount = useMemo(() => {
    return getPreferencesSetCount(preferences);
  }, [preferences]);

  const Icon = variant === 'warning' ? AlertCircle : Check;
  const iconColor = variant === 'warning' ? 'text-amber-500' : 'text-primary';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mx-auto flex items-center justify-center gap-2 rounded-full text-gray-700 transition-colors hover:text-gray-900"
      >
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="text-sm font-medium">
          {preferencesCount === 0 ? 'No preferences set' : `${preferencesCount} preferences set`}
        </span>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-4 overflow-hidden"
          >
            <div className="bg-primary-50 border-primary-200 rounded-lg border px-6 py-5">
              {/* Warning message at the top if variant is warning */}
              {variant === 'warning' && warningMessage && (
                <div className="mb-4 space-y-1 border-b border-amber-200 pb-4">
                  <h3 className="text-sm font-medium text-amber-800">{warningMessage.title}</h3>
                  <p className="text-sm text-amber-700">{warningMessage.description}</p>
                </div>
              )}

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-700">Do you care about death benefits: </span>
                    <span className="font-semibold text-gray-900">
                      {getPreferenceLabel('deathBenefit', preferences.deathBenefit)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-700">How do you prefer to pay: </span>
                    <span className="font-semibold text-gray-900">
                      {getPreferenceLabel('paymentStructure', preferences.paymentStructure)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-700">What type of benefit structure: </span>
                    <span className="font-semibold text-gray-900">
                      {getPreferenceLabel('benefitStructure', preferences.benefitStructure)}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="text-primary hover:text-primary/80 shrink-0"
                >
                  Edit
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
